import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { X, Plus } from 'lucide-react';
import {
  type SecondaryTag,
  searchSecondaryTags,
  listTopSecondaryTags,
  getSongSecondaryTags,
  attachSecondaryTag,
  removeSecondaryTag,
  createAndAttachSecondaryTag,
} from '../lib/supabase/songs';

interface SecondaryTagsInputProps {
  songId?: string;
  value?: string[]; // Tag names for local mode (when no songId)
  onChange?: (tagNames: string[]) => void; // Callback for local mode
}

export default function SecondaryTagsInput({ songId, value, onChange }: SecondaryTagsInputProps) {
  const [selectedTags, setSelectedTags] = useState<SecondaryTag[]>([]);
  const [localTagNames, setLocalTagNames] = useState<string[]>(value || []);
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SecondaryTag[]>([]);
  const [topTags, setTopTags] = useState<SecondaryTag[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isLocalMode = !songId;

  useEffect(() => {
    if (songId) {
      loadSongTags();
    }
    loadTopTags();
  }, [songId]);

  useEffect(() => {
    if (value && isLocalMode) {
      setLocalTagNames(value);
    }
  }, [value, isLocalMode]);

  useEffect(() => {
    if (inputValue.trim().length > 0) {
      performSearch(inputValue);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [inputValue]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadSongTags() {
    if (!songId) return;
    const { data } = await getSongSecondaryTags(songId);
    if (data) {
      setSelectedTags(data);
    }
  }

  async function loadTopTags() {
    const { data } = await listTopSecondaryTags(12);
    if (data) {
      setTopTags(data);
    }
  }

  async function performSearch(query: string) {
    setIsLoading(true);
    const { data } = await searchSecondaryTags(query);
    if (data) {
      const filtered = isLocalMode
        ? data.filter(tag => !localTagNames.includes(tag.name))
        : data.filter(tag => !selectedTags.some(selected => selected.id === tag.id));
      setSearchResults(filtered);
      setShowDropdown(true);
      setHighlightedIndex(0);
    }
    setIsLoading(false);
  }

  async function handleAddTag(tag: SecondaryTag) {
    if (isLocalMode) {
      const newTagNames = [...localTagNames, tag.name.toLowerCase()];
      setLocalTagNames(newTagNames);
      onChange?.(newTagNames);
    } else {
      const { error } = await attachSecondaryTag(songId!, tag.id);
      if (!error) {
        setSelectedTags([...selectedTags, tag]);
        await loadTopTags();
      }
    }

    setInputValue('');
    setShowDropdown(false);
  }

  async function handleCreateTag(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;

    if (isLocalMode) {
      const newTagNames = [...localTagNames, trimmed.toLowerCase()];
      setLocalTagNames(newTagNames);
      onChange?.(newTagNames);
    } else {
      const { data: newTag, error } = await createAndAttachSecondaryTag(songId!, trimmed);
      if (!error && newTag) {
        setSelectedTags([...selectedTags, newTag]);
        await loadTopTags();
      }
    }

    setInputValue('');
    setShowDropdown(false);
  }

  async function handleRemoveTag(tag: SecondaryTag | string) {
    if (isLocalMode) {
      const tagName = typeof tag === 'string' ? tag : tag.name;
      const newTagNames = localTagNames.filter(name => name !== tagName);
      setLocalTagNames(newTagNames);
      onChange?.(newTagNames);
    } else {
      const tagObj = tag as SecondaryTag;
      const { error } = await removeSecondaryTag(songId!, tagObj.id);
      if (!error) {
        setSelectedTags(selectedTags.filter(t => t.id !== tagObj.id));
        await loadTopTags();
      }
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!showDropdown) {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (inputValue.trim()) {
          handleCreateTag(inputValue);
        }
      }
      return;
    }

    const items = getDropdownItems();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + items.length) % items.length);
        break;
      case 'Enter':
        e.preventDefault();
        const selectedItem = items[highlightedIndex];
        if (selectedItem) {
          if (selectedItem.type === 'create') {
            handleCreateTag(inputValue);
          } else {
            handleAddTag(selectedItem.tag);
          }
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setInputValue('');
        break;
    }
  }

  function getDropdownItems() {
    const items: Array<{ type: 'tag' | 'create'; tag?: SecondaryTag }> = [];

    searchResults.forEach(tag => {
      items.push({ type: 'tag', tag });
    });

    if (inputValue.trim() && !searchResults.some(t => t.name.toLowerCase() === inputValue.toLowerCase())) {
      items.push({ type: 'create' });
    }

    return items;
  }

  const availableTopTags = topTags.filter(
    tag => isLocalMode
      ? !localTagNames.includes(tag.name)
      : !selectedTags.some(selected => selected.id === tag.id)
  );

  const displayTags = isLocalMode ? localTagNames : selectedTags;

  return (
    <div className="space-y-3">
      <label className="block text-[13px] font-medium text-[#A0A0A0]">
        Secondary Tags <span className="text-[#606060] font-normal">(internal, optional)</span>
      </label>

      <div className="relative">
        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {displayTags.map((tag) => {
              const tagName = (typeof tag === 'string' ? tag : tag.name).toLowerCase();
              const tagKey = typeof tag === 'string' ? tag : tag.id;
              return (
                <div
                  key={tagKey}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#282828] rounded-lg text-[12px] text-[#F5F5F5]"
                >
                  <span>{tagName}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-[#FF6B6B] transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim()) {
              setShowDropdown(true);
            }
          }}
          placeholder="Type to search tags..."
          className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[13px] text-[#F5F5F5] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]/50"
        />

        {showDropdown && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-[#1A1A1A] border border-[#282828] rounded-lg shadow-2xl max-h-[280px] overflow-y-auto"
          >
            {isLoading ? (
              <div className="px-4 py-3 text-[12px] text-[#808080]">
                Searching...
              </div>
            ) : getDropdownItems().length === 0 ? (
              <div className="px-4 py-3 text-[12px] text-[#808080]">
                No tags found
              </div>
            ) : (
              getDropdownItems().map((item, index) => {
                if (item.type === 'create') {
                  return (
                    <button
                      key="create"
                      type="button"
                      onClick={() => handleCreateTag(inputValue)}
                      className={`w-full px-4 py-2.5 text-left flex items-center gap-2 transition-colors ${
                        index === highlightedIndex
                          ? 'bg-[#40E0D0]/10 text-[#40E0D0]'
                          : 'text-[#A0A0A0] hover:bg-[#282828]'
                      }`}
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-[13px]">
                        Create tag <span className="font-medium">"{inputValue}"</span>
                      </span>
                    </button>
                  );
                } else if (item.tag) {
                  return (
                    <button
                      key={item.tag.id}
                      type="button"
                      onClick={() => handleAddTag(item.tag!)}
                      className={`w-full px-4 py-2.5 text-left transition-colors ${
                        index === highlightedIndex
                          ? 'bg-[#40E0D0]/10'
                          : 'hover:bg-[#282828]'
                      }`}
                    >
                      <span className="text-[13px] text-[#F5F5F5]">{item.tag.name.toLowerCase()}</span>
                    </button>
                  );
                }
                return null;
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
